// Query

import { keepPreviousData, useMutation, useQuery } from '@tanstack/vue-query';
import { computed, ComputedRef, Ref, WritableComputedRef } from 'vue';
import {
    getInteractions,
    getInteractionTable,
    updateInteractionTableLayout,
} from '../services/api/interaction/interactionAPI';
import {
    ITableInteraction,
    IRowCell,
    ISortOption,
    ResponseRowCell,
    RequestUpdateTableLayout,
} from '../services/api/interaction/types';
import { QK } from '../consts/query-keys';
import { EchoFilterRequest } from '../services/api/filter/types';

// -- Queries
export const useGetInteractionTable = (
    page: WritableComputedRef<number>,
    pageSize: WritableComputedRef<number>,
    sortFieldId: WritableComputedRef<string | null>,
    sortOrder: WritableComputedRef<string | null>,
    filter: Ref<EchoFilterRequest>
) => {
    const sortOption = computed<ISortOption | undefined>(() => {
        const column = sortFieldId.value;
        if (!column) return;
        const dir = String(sortOrder.value).toLowerCase() === 'asc' ? 'Asc' : 'Desc';
        return { columnId: column, sortDirection: dir };
    });

    const queryKey = computed(() => [
        QK.INTERACTIONS.TABLE,
        page.value,
        pageSize.value,
        sortOrder.value ?? '',
        sortFieldId.value ?? '',
        filter.value,
    ]);

    return useQuery({
        queryKey,
        queryFn: async () =>
            await getInteractionTable({
                page: page.value,
                pageSize: pageSize.value,
                sortOption: sortOption.value,
                filter: filter.value,
            }),
        select: (data): ITableInteraction => {
            const { rowHeaders, ...interactionObject } = data;
            const convertedRowCells: IRowCell[] = data.rowCells.reduce(
                (acc: any, item: ResponseRowCell) => {
                    const { rowIndex, columnId, value } = item;

                    if (!acc[rowIndex]) {
                        acc[rowIndex] = { index: rowIndex };
                    }

                    const headerColum = data.columnHeaders.find((c) => c.id === columnId);
                    const rowHeaderData = data.rowHeaders.find((r) => r.index === rowIndex);

                    if (!headerColum || !rowHeaderData) {
                        return acc;
                    }

                    const { index, ...rowHeader } = rowHeaderData;

                    acc[rowIndex][
                        headerColum?.translationKey.split('table-interaction-column-')[1]
                    ] = value;
                    acc[rowIndex] = { ...acc[rowIndex], ...rowHeader };

                    return acc;
                },
                []
            );

            return { ...interactionObject, rowCells: convertedRowCells };
        },
        placeholderData: keepPreviousData,
    });
};

export const updateTableLayout = () => {
    return useMutation({
        mutationFn: async (columns: RequestUpdateTableLayout['columns']) =>
            await updateInteractionTableLayout({ columns, tableKey: 'Interactions' }),
    });
};

export const useGetInteractions = (ids: ComputedRef<string[]>) => {
    return useQuery({
        queryKey: computed(() => [QK.INTERACTIONS.GET, ...ids.value]),
        queryFn: async () => await getInteractions({ ids: ids.value }),
        enabled: computed(() => ids.value.length > 0),
    });
};
